package app.footyguide.web.page

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.core.locale.LocalStrings
import app.footyguide.core.theme.ClubCatalog
import app.footyguide.domain.model.Game
import app.footyguide.domain.model.GameRepository
import app.footyguide.domain.model.MatchStatus
import app.footyguide.web.components.AppToolbar
import app.footyguide.web.components.CrestSizes
import app.footyguide.web.components.Icons
import app.footyguide.web.components.SafeImg
import app.footyguide.web.components.club.*
import app.footyguide.web.data.StandingRow
import app.footyguide.web.data.WebGameRepository
import app.footyguide.web.theme.FootyTheme
import app.footyguide.web.todayFormatted
import kotlinx.browser.window

/**
 * Dedicated Club Detail Page (e.g. /time/atleticomg, /time/flamengo, /time/palmeiras).
 * - 100% full-width AppToolbar with explicit Back button
 * - NO bottom navigation bar
 * - Reusable modular club components with zero code duplication
 * - Direct navigation to opponent clubs on click
 */
@Composable
fun ClubDetailPage(
    clubSlug: String,
    gameRepository: GameRepository = remember { WebGameRepository() },
    onNavigate: (String) -> Unit = {},
    onBack: () -> Unit = {}
) {
    val strings = LocalStrings.current
    val club = ClubCatalog.bySlug(clubSlug) ?: ClubCatalog.CLUBS.first { it.slug == "flamengo" }
    var activeSubTab by remember { mutableStateOf(ClubSubTab.OVERVIEW) }
    var isFavorite by remember(clubSlug) {
        mutableStateOf(window.localStorage.getItem("fg-my-team") == club.slug)
    }

    var teamGames by remember(club.slug) { mutableStateOf<List<Game>>(emptyList()) }
    var standings by remember(club.slug) { mutableStateOf<List<StandingRow>>(emptyList()) }
    var isLoading by remember(club.slug) { mutableStateOf(true) }

    LaunchedEffect(club.slug) {
        isLoading = true
        try {
            val rawGames = gameRepository.getGamesByTeam(club.slug, "2026-01-01", "2026-12-31")
            teamGames = deduplicateTeamSeasonGames(rawGames)
        } catch (_: Exception) {
            teamGames = emptyList()
        }
        try {
            standings = app.footyguide.web.data.WebStandingsRepository.getStandings("brasileirao", gameRepository)
        } catch (_: Exception) {
            standings = emptyList()
        }
        isLoading = false
    }

    val today = remember { todayFormatted() }
    val recentMatches = remember(teamGames, today) {
        teamGames.filter { it.date < today || it.matchStatus == MatchStatus.FINISHED }
            .sortedBy { it.kickoff }
            .takeLast(5)
            .reversed()
    }
    val upcomingMatches = remember(teamGames, today) {
        teamGames.filter { it.date >= today && it.matchStatus != MatchStatus.FINISHED }
            .sortedBy { it.kickoff }
    }
    val nextGame = upcomingMatches.firstOrNull()

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            minHeight(100.vh)
            backgroundColor(Color("#FAFAF7"))
            paddingBottom(60.px)
        }
    }) {
        // FULL-WIDTH TOOLBAR WITH BACK BUTTON AND FAVORITE ACTION
        AppToolbar(
            onBack = onBack,
            icon = {
                SafeImg(
                    src = club.logoUrl,
                    fallbackIcon = Icons.SHIELD,
                    fallbackSize = CrestSizes.TOOLBAR_FALLBACK,
                    modifier = {
                        width(CrestSizes.TOOLBAR_PX.px)
                        height(CrestSizes.TOOLBAR_PX.px)
                        property("width", CrestSizes.TOOLBAR_CSS)
                        property("height", CrestSizes.TOOLBAR_CSS)
                        property("object-fit", "contain")
                    }
                )
            },
            title = club.name,
            actions = {
                Button({
                    onClick {
                        window.localStorage.setItem("fg-my-team", club.slug)
                        FootyTheme.fromTeamColors(club.c1, club.c2, club.c3).apply()
                        isFavorite = true
                    }
                    style {
                        fontSize(11.px)
                        fontWeight(700)
                        fontFamily("Inter, sans-serif")
                        color(Color(if (isFavorite) "var(--accent-on-fill, #FFFFFF)" else club.c1))
                        backgroundColor(Color(if (isFavorite) club.c1 else "#FFFFFF"))
                        border(1.px, LineStyle.Solid, Color(if (isFavorite) club.c1 else "#E7E7E1"))
                        padding(5.px, 12.px)
                        borderRadius(999.px)
                        cursor("pointer")
                        property("transition", "all 0.2s ease")
                    }
                }) {
                    Text(if (isFavorite) strings.myTeamStarred else strings.favoriteAction)
                }
            }
        )

        // MAIN CONTENT CONTAINER
        Div({
            style {
                width(100.percent)
                maxWidth(600.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                backgroundColor(Color("#FAFAF7"))
            }
        }) {
            ClubHeroBanner(club)

            ClubSubTabsRow(
                activeTab = activeSubTab,
                onTabSelected = { activeSubTab = it },
                strings = strings
            )

            if (isLoading) {
                ClubHubSkeleton()
            } else {
                when (activeSubTab) {
                    ClubSubTab.OVERVIEW -> {
                        ClubNextMatchCard(club = club, nextGame = nextGame, onNavigate = onNavigate, strings = strings)
                        ClubRecentResultsList(club = club, matches = recentMatches, onNavigate = onNavigate, strings = strings)
                        ClubQuizCard(club = club)
                    }
                    ClubSubTab.CALENDAR -> {
                        ClubCalendarList(club = club, matches = upcomingMatches, onNavigate = onNavigate, strings = strings)
                    }
                    ClubSubTab.STANDINGS -> {
                        ClubStandingsTable(club = club, standings = standings, onNavigate = onNavigate, strings = strings)
                    }
                }
            }
        }
    }
}
